import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

const AdminRegions = () => {
  const [regions,     setRegions]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [newName,     setNewName]     = useState('');
  const [adding,      setAdding]      = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [editName,    setEditName]    = useState('');
  const [deleteId,    setDeleteId]    = useState(null);
  const [deleteName,  setDeleteName]  = useState('');
  const toast = useToast();

  const fetchRegions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getRegions();
      setRegions(res.data);
    } catch {
      toast.error('Errore nel caricamento delle regioni.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchRegions(); }, [fetchRegions]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.createRegion(name);
      toast.success('Regione creata.');
      setNewName('');
      fetchRegions();
    } catch (err) {
      toast.error(err.message || 'Errore durante la creazione.');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (region) => {
    setEditId(region.id);
    setEditName(region.name);
  };

  const handleUpdate = async (e, id) => {
    e.preventDefault();
    const name = editName.trim();
    if (!name) return;
    try {
      await api.updateRegion(id, name);
      toast.success('Regione aggiornata.');
      setEditId(null);
      fetchRegions();
    } catch (err) {
      toast.error(err.message || 'Errore durante l\'aggiornamento.');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleRegion(id);
      fetchRegions();
    } catch {
      toast.error('Errore durante l\'aggiornamento.');
    }
  };

  const confirmDelete = (region) => {
    setDeleteId(region.id);
    setDeleteName(region.name);
  };

  const handleDelete = async () => {
    try {
      await api.deleteRegion(deleteId);
      toast.success('Regione eliminata.');
      setDeleteId(null);
      fetchRegions();
    } catch (err) {
      toast.error(err.message || 'Errore durante l\'eliminazione.');
      setDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <div className="text-gray-500 text-sm">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl text-white font-semibold">Regioni</h1>
        <p className="text-gray-500 text-sm mt-0.5">{regions.length} regioni totali</p>
      </div>

      {/* Form nuova regione */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-6">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Nome nuova regione"
          className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white transition"
        />
        <button
          type="submit"
          disabled={adding || !newName.trim()}
          className="px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Aggiungi
        </button>
      </form>

      {/* Lista regioni */}
      <div className="space-y-2">
        {regions.map(region => (
          <div
            key={region.id}
            className={`flex items-center gap-3 bg-gray-900 border rounded-lg px-4 py-3 ${
              region.is_active ? 'border-gray-700' : 'border-gray-800 opacity-60'
            }`}
          >
            {/* Nome / Edit inline */}
            {editId === region.id ? (
              <form onSubmit={e => handleUpdate(e, region.id)} className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  autoFocus
                  className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-white"
                />
                <button
                  type="submit"
                  className="px-3 py-1 bg-white text-black rounded text-xs font-medium hover:bg-gray-100 transition"
                >
                  Salva
                </button>
                <button
                  type="button"
                  onClick={() => setEditId(null)}
                  className="px-3 py-1 border border-gray-600 text-gray-400 rounded text-xs hover:border-white hover:text-white transition"
                >
                  Annulla
                </button>
              </form>
            ) : (
              <span className="flex-1 text-white text-sm">{region.name}</span>
            )}

            {/* Stato badge */}
            <span className={`text-xs px-2 py-0.5 rounded ${
              region.is_active ? 'bg-green-900/50 text-green-400' : 'bg-gray-800 text-gray-500'
            }`}>
              {region.is_active ? 'Attiva' : 'Inattiva'}
            </span>

            {/* Azioni */}
            {editId !== region.id && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => startEdit(region)}
                  title="Modifica"
                  className="p-1.5 text-gray-400 hover:text-white transition rounded"
                >
                  ✎
                </button>
                <button
                  onClick={() => handleToggle(region.id)}
                  title={region.is_active ? 'Disattiva' : 'Attiva'}
                  className="p-1.5 text-gray-400 hover:text-yellow-400 transition rounded text-xs"
                >
                  {region.is_active ? '⏸' : '▶'}
                </button>
                <button
                  onClick={() => confirmDelete(region)}
                  title="Elimina"
                  className="p-1.5 text-gray-400 hover:text-red-400 transition rounded"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        ))}

        {regions.length === 0 && (
          <p className="text-gray-600 text-sm text-center py-8">
            Nessuna regione. Aggiungine una sopra.
          </p>
        )}
      </div>

      {/* Confirm delete */}
      {deleteId && (
        <ConfirmDialog
          message={`Sei sicuro di voler eliminare la regione "${deleteName}"? L'operazione è bloccata se ci sono brand associati.`}
          confirmLabel="Elimina"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};

export default AdminRegions;
